<template>
  <div class="attendance-monitoring">

    <!-- Page header -->
    <div class="mon-header">
      <div class="mon-header-left">
        <div class="mon-header-icon">
          <i class="bi bi-display"></i>
        </div>
        <div>
          <h5 class="mon-title mb-0">Attendance Monitoring</h5>
          <small class="mon-date">{{ formattedDate }} &nbsp;·&nbsp; auto-refreshes every minute</small>
        </div>
      </div>
      <div class="mon-header-actions">
        <button class="btn-refresh" :class="{ spinning: loading }" @click="load">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-bar">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="summary-card"
        :class="[`summary-${card.key}`, { active: activeTab === card.tab }]"
        role="button"
        tabindex="0"
        :aria-pressed="activeTab === card.tab"
        @click="activeTab = card.tab"
        @keyup.enter="activeTab = card.tab"
      >
        <div class="summary-top">
          <span class="summary-count">{{ card.count }}</span>
          <span v-if="card.key !== 'total'" class="summary-pct">{{ card.pct }}%</span>
        </div>
        <div class="summary-label">
          <span class="summary-dot" :class="`dot-${card.key}`"></span>{{ card.label }}
        </div>
        <div class="summary-meter" :class="`meter-${card.key}`">
          <span :style="{ width: card.key === 'total' ? '100%' : `${card.pct}%` }"></span>
        </div>
      </div>
    </div>

    <!-- Analytics -->
    <div v-if="summary.total" class="analytics-panel">
      <div class="analytics-card insights-card">
        <div class="analytics-head">
          <h6>Today's snapshot</h6>
          <span class="analytics-sub">Live attendance overview</span>
        </div>
        <div class="insight-grid">
          <div class="insight-item">
            <span class="insight-value">{{ analytics.punchRate }}%</span>
            <span class="insight-label">Punched today</span>
            <span class="insight-note">{{ analytics.punched }} of {{ summary.total }} staff</span>
          </div>
          <div class="insight-item">
            <span class="insight-value insight-in">{{ analytics.presenceRate }}%</span>
            <span class="insight-label">Currently in office</span>
            <span class="insight-note">{{ summary.in }} staff on-site</span>
          </div>
          <div class="insight-item">
            <span class="insight-value insight-warn">{{ analytics.lateCount }}</span>
            <span class="insight-label">Schedule violations</span>
            <span class="insight-note">Late or early punches today</span>
          </div>
          <div class="insight-item">
            <span class="insight-value insight-leave">{{ analytics.awayCount }}</span>
            <span class="insight-label">On leave / pass slip</span>
            <span class="insight-note">{{ summary.on_leave + summary.on_pass_slip + summary.on_activity }} away</span>
          </div>
          <div class="insight-item">
            <span class="insight-value">{{ analytics.monthPassSlips }}</span>
            <span class="insight-label">Pass slips (month)</span>
            <span class="insight-note">Approved this month</span>
          </div>
          <div class="insight-item">
            <span class="insight-value insight-warn">{{ analytics.monthLateStaff }}</span>
            <span class="insight-label">Staff late (month)</span>
            <span class="insight-note">At least one late morning</span>
          </div>
        </div>
      </div>

      <div class="analytics-card chart-card">
        <div class="analytics-head">
          <h6>Status mix</h6>
          <span class="analytics-sub">Share of total staff</span>
        </div>
        <div class="chart-wrap chart-donut">
          <canvas ref="statusChart"></canvas>
        </div>
      </div>

      <div class="analytics-card chart-card chart-wide">
        <div class="analytics-head">
          <h6>By office</h6>
          <span class="analytics-sub">Attendance per assigned office</span>
        </div>
        <div class="chart-wrap chart-bar" :style="{ height: officeChartHeight + 'px' }">
          <canvas ref="officeChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Search + tab bar (sticks to the top while scrolling the list) -->
    <div class="controls-sticky">
    <div class="mon-controls">
      <div class="mon-tabs">
        <button class="mon-tab" :class="{ active: activeTab === 'all' }" @click="activeTab = 'all'">All</button>
        <button class="mon-tab" :class="{ active: activeTab === 'in' }" @click="activeTab = 'in'">
          In Office
          <span class="mon-tab-badge badge-in">{{ summary.in }}</span>
        </button>
        <button class="mon-tab" :class="{ active: activeTab === 'out' }" @click="activeTab = 'out'">
          Left
          <span class="mon-tab-badge badge-out">{{ summary.out }}</span>
        </button>
        <button class="mon-tab" :class="{ active: activeTab === 'absent' }" @click="activeTab = 'absent'">
          No Record
          <span class="mon-tab-badge badge-absent">{{ summary.absent }}</span>
        </button>
        <button class="mon-tab" :class="{ active: activeTab === 'on_leave' }" @click="activeTab = 'on_leave'">
          On Leave
          <span class="mon-tab-badge badge-on_leave">{{ summary.on_leave }}</span>
        </button>
        <button class="mon-tab" :class="{ active: activeTab === 'on_pass_slip' }" @click="activeTab = 'on_pass_slip'">
          Pass Slip
          <span class="mon-tab-badge badge-on_pass_slip">{{ summary.on_pass_slip }}</span>
        </button>
        <button class="mon-tab" :class="{ active: activeTab === 'on_activity' }" @click="activeTab = 'on_activity'">
          On Activity
          <span class="mon-tab-badge badge-on_activity">{{ summary.on_activity }}</span>
        </button>
      </div>
      <div class="mon-controls-right">
        <select v-model="officeFilter" class="mon-office-filter">
          <option value="">All offices</option>
          <option v-for="office in offices" :key="office" :value="office">{{ office }}</option>
        </select>
        <div class="mon-search-wrap">
          <i class="bi bi-search mon-search-icon"></i>
          <input v-model="search" class="mon-search" placeholder="Search name..." />
        </div>
      </div>
    </div>

    <div v-if="lastRefreshed" class="mon-meta">
      <span><i class="bi bi-clock-history mr-1"></i>Updated {{ lastRefreshed }}</span>
      <span>{{ filtered.length }} shown</span>
    </div>
    </div>

    <!-- First load — skeleton placeholders (no flash on the 60s auto-refresh) -->
    <div v-if="loading && !hasLoadedOnce" class="emp-grid" aria-hidden="true">
      <div v-for="n in skeletonCount" :key="'sk' + n" class="emp-skeleton">
        <div class="sk-top">
          <span class="sk-block sk-avatar"></span>
          <div class="sk-lines">
            <span class="sk-block sk-line"></span>
            <span class="sk-block sk-line sk-line-sm"></span>
          </div>
        </div>
        <div class="sk-board">
          <span class="sk-block"></span>
          <span class="sk-block"></span>
          <span class="sk-block"></span>
          <span class="sk-block"></span>
        </div>
        <span class="sk-block sk-foot"></span>
      </div>
    </div>

    <!-- Employee list -->
    <div
      v-else-if="filtered.length"
      class="emp-grid"
      :class="{ 'emp-grid-refreshing': loading }"
    >
      <article
        v-for="emp in filtered"
        :key="emp.personnel_id"
        class="emp-card"
        :class="`emp-card-${emp.status}`"
        @click="openDetail(emp)"
      >
        <div class="emp-card-top">
          <div class="emp-avatar-wrap">
            <div class="emp-avatar">
              <img
                v-if="showAvatar(emp)"
                :src="avatarUrl(emp.personnel_id)"
                :alt="emp.first_name"
                class="emp-avatar-img"
                @error="onAvatarError(emp)"
              />
              <span v-else class="emp-avatar-fallback" :style="avatarStyle(emp)">
                {{ initials(emp) }}
              </span>
            </div>
          </div>

          <div class="emp-identity">
            <div class="emp-name-row">
              <h6 class="emp-name">{{ emp.last_name }}, {{ emp.first_name }}</h6>
              <span v-if="emp.is_late" class="emp-late-badge">Late</span>
              <span v-if="emp.has_web_punch" class="emp-web-badge" title="Has system (web) punch today">
                <i class="bi bi-phone"></i> System
              </span>
            </div>
            <p class="emp-office">
              <i :class="emp.status === 'on_activity' ? 'bi bi-calendar-event' : 'bi bi-building'"></i>
              <template v-if="emp.status === 'on_leave' && emp.leave_category">
                {{ emp.leave_category }} leave
              </template>
              <template v-else-if="emp.status === 'on_activity' && emp.activity_title">
                {{ emp.activity_title }}
              </template>
              <template v-else>{{ emp.office || "Unassigned office" }}</template>
            </p>
          </div>

          <span class="emp-status-pill" :class="`status-${emp.status}`">
            <span class="emp-status-dot" :class="`dot-${emp.status}`"></span>
            {{ statusLabel(emp.status) }}
          </span>
        </div>

        <div class="emp-punch-board">
          <div
            v-for="(slot, idx) in punchSlots"
            :key="slot.key"
            class="punch-cell"
            :class="punchCellClass(emp, idx)"
            :title="slot.label"
          >
            <span class="punch-cell-label">{{ slot.short }}</span>
            <span class="punch-cell-time">
              {{ punchTime(emp, idx) }}
            </span>
          </div>
        </div>

        <div class="emp-card-foot">
          <div class="emp-foot-times">
            <span v-if="emp.time_in" class="emp-time emp-time-in">
              <i class="bi bi-box-arrow-in-right"></i> In {{ formatTime(emp.time_in) }}
            </span>
            <span v-if="emp.time_out" class="emp-time emp-time-out">
              <i class="bi bi-box-arrow-right"></i> Out {{ formatTime(emp.time_out) }}
            </span>
            <span v-if="!emp.time_in && !emp.time_out && emp.status === 'on_activity'" class="emp-time emp-time-activity">
              <i class="bi bi-calendar-event"></i> Out for activity
            </span>
            <span v-else-if="!emp.time_in && !emp.time_out" class="emp-time emp-time-muted">
              No punches recorded today
            </span>
          </div>
          <span class="emp-view-link">
            View details <i class="bi bi-chevron-right"></i>
          </span>
        </div>

        <div class="emp-month-stats">
          <span
            class="emp-stat-chip"
            :class="{ 'emp-stat-active': emp.pass_slip_count_month > 0 }"
            title="Approved pass slips this month"
          >
            <i class="bi bi-door-open"></i>
            {{ emp.pass_slip_count_month || 0 }} pass slip{{ (emp.pass_slip_count_month || 0) === 1 ? '' : 's' }}
          </span>
          <span
            class="emp-stat-chip"
            :class="{ 'emp-stat-warn': emp.late_days_month > 0 }"
            title="Days late this month (morning in after 8:00 AM)"
          >
            <i class="bi bi-alarm"></i>
            Late {{ emp.late_days_month || 0 }}×
          </span>
        </div>
      </article>
    </div>

    <!-- Detail modal -->
    <transition name="modal-fade">
      <div v-if="detail" class="det-backdrop" @click.self="closeDetail">
        <div class="det-modal">
          <!-- Header -->
          <div class="det-header">
            <div class="det-avatar" :class="`avatar-${detail.status}`">
              <img
                v-if="showAvatar(detail)"
                :src="avatarUrl(detail.personnel_id)"
                :alt="detail.first_name"
                class="emp-avatar-img"
                @error="onAvatarError(detail)"
              />
              <span v-else class="emp-avatar-fallback" :style="avatarStyle(detail)">
                {{ initials(detail) }}
              </span>
            </div>
            <div class="det-identity">
              <div class="det-name">{{ detail.last_name }}, {{ detail.first_name }}</div>
              <div class="det-meta">
                <span v-if="detail.office" class="det-div">{{ detail.office }}</span>
                <span class="det-status-badge" :class="`status-${detail.status}`">
                  {{ statusLabel(detail.status) }}
                </span>
                <span v-if="detail.status === 'on_leave' && detail.leave_category" class="det-status-badge status-on_leave">
                  {{ detail.leave_category }}
                </span>
                <span v-if="detail.status === 'on_activity' && detail.activity_title" class="det-activity-title">
                  <i class="bi bi-calendar-event mr-1"></i>{{ detail.activity_title }}
                </span>
              </div>
            </div>
            <button class="det-close" @click="closeDetail"><i class="bi bi-x-lg"></i></button>
          </div>

          <!-- Punch timeline -->
          <div class="det-body">
            <div class="det-month-bar">
              <div class="det-month-stat">
                <span class="det-month-value">{{ detail.pass_slip_count_month || 0 }}</span>
                <span class="det-month-label">Pass slips this month</span>
              </div>
              <div class="det-month-stat">
                <span class="det-month-value" :class="{ 'text-danger': detail.late_days_month > 0 }">
                  {{ detail.late_days_month || 0 }}
                </span>
                <span class="det-month-label">Late days this month</span>
              </div>
            </div>

            <div class="punch-grid">
              <div class="punch-slot" :class="[detail.punches[0] ? 'slot-filled' : 'slot-empty', violation(detail, 'morning_late') && 'slot-violation']">
                <div class="punch-label"><i class="bi bi-sunrise mr-1"></i>Morning In</div>
                <div class="punch-time">{{ detail.punches[0] ? formatTime(detail.punches[0]) : '—' }}</div>
                <div v-if="violation(detail, 'morning_late')" class="punch-violation">Late (after 8:00 AM)</div>
              </div>
              <div class="punch-slot" :class="[detail.punches[1] ? 'slot-filled slot-out' : 'slot-empty', violation(detail, 'lunch_out_early') && 'slot-violation']">
                <div class="punch-label"><i class="bi bi-box-arrow-right mr-1"></i>Lunch Out</div>
                <div class="punch-time">{{ detail.punches[1] ? formatTime(detail.punches[1]) : '—' }}</div>
                <div v-if="violation(detail, 'lunch_out_early')" class="punch-violation">Early (before 12:00 PM)</div>
              </div>
              <div class="punch-slot" :class="[detail.punches[2] ? 'slot-filled' : 'slot-empty', violation(detail, 'lunch_in_late') && 'slot-violation']">
                <div class="punch-label"><i class="bi bi-box-arrow-in-right mr-1"></i>Lunch In</div>
                <div class="punch-time">{{ detail.punches[2] ? formatTime(detail.punches[2]) : '—' }}</div>
                <div v-if="violation(detail, 'lunch_in_late')" class="punch-violation">{{ lunchInLateLabel(detail) }}</div>
              </div>
              <div class="punch-slot" :class="[detail.punches[3] ? 'slot-filled slot-out' : 'slot-empty', violation(detail, 'evening_out_early') && 'slot-violation']">
                <div class="punch-label"><i class="bi bi-sunset mr-1"></i>Evening Out</div>
                <div class="punch-time">{{ detail.punches[3] ? formatTime(detail.punches[3]) : '—' }}</div>
                <div v-if="violation(detail, 'evening_out_early')" class="punch-violation">Early (before 7:00 PM)</div>
              </div>
            </div>

            <!-- Lunch punch photos -->
            <div class="det-section">
              <div class="extra-label">Lunch punch photos</div>
              <div class="lunch-photo-grid">
                <div class="lunch-photo-card">
                  <div class="lunch-photo-head">
                    <span>Lunch Out</span>
                    <small v-if="detail.lunch_photos && detail.lunch_photos.out && detail.lunch_photos.out.time">
                      {{ formatTime(detail.lunch_photos.out.time) }}
                    </small>
                  </div>
                  <div class="lunch-photo-body">
                    <img
                      v-if="photoUrls.out"
                      :src="photoUrls.out"
                      alt="Lunch out"
                      class="lunch-photo-img"
                    />
                    <div v-else-if="photoLoading" class="lunch-photo-empty">Loading…</div>
                    <div v-else class="lunch-photo-empty">
                      {{ lunchPhotoEmptyLabel(detail, 'out') }}
                    </div>
                  </div>
                </div>
                <div class="lunch-photo-card">
                  <div class="lunch-photo-head">
                    <span>Lunch In</span>
                    <small v-if="detail.lunch_photos && detail.lunch_photos.in && detail.lunch_photos.in.time">
                      {{ formatTime(detail.lunch_photos.in.time) }}
                    </small>
                  </div>
                  <div class="lunch-photo-body">
                    <img
                      v-if="photoUrls.in"
                      :src="photoUrls.in"
                      alt="Lunch in"
                      class="lunch-photo-img"
                    />
                    <div v-else-if="photoLoading" class="lunch-photo-empty">Loading…</div>
                    <div v-else class="lunch-photo-empty">
                      {{ lunchPhotoEmptyLabel(detail, 'in') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Today's pass slip -->
            <div v-if="detail.pass_slip_today" class="det-section">
              <div class="extra-label">Today's pass slip</div>
              <div class="pass-slip-card">
                <div class="pass-slip-row">
                  <span class="pass-slip-k">Reference</span>
                  <span class="pass-slip-v">{{ detail.pass_slip_today.reference_no || '—' }}</span>
                </div>
                <div class="pass-slip-row">
                  <span class="pass-slip-k">Authorized out / in</span>
                  <span class="pass-slip-v">
                    {{ formatTime(detail.pass_slip_today.time_out) || '—' }}
                    →
                    {{ detail.pass_slip_today.do_not_return ? 'Do not return' : (formatTime(detail.pass_slip_today.time_in) || '—') }}
                  </span>
                </div>
                <div v-if="detail.pass_slip_today.reason" class="pass-slip-row">
                  <span class="pass-slip-k">Reason</span>
                  <span class="pass-slip-v">{{ detail.pass_slip_today.reason }}</span>
                </div>
                <div class="pass-slip-row">
                  <span class="pass-slip-k">Return</span>
                  <span class="pass-slip-v">
                    {{ detail.pass_slip_today.return_confirmed_at ? 'Confirmed' : (detail.pass_slip_today.do_not_return ? 'N/A' : 'Pending') }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Month pass slips -->
            <div class="det-section">
              <div class="extra-label">Pass slips this month</div>
              <div v-if="passSlipsLoading" class="text-muted small">Loading pass slips…</div>
              <div v-else-if="!monthPassSlips.length" class="text-muted small">No approved pass slips this month.</div>
              <div v-else class="pass-slip-list">
                <div
                  v-for="slip in monthPassSlips"
                  :key="slip.id"
                  class="pass-slip-card"
                >
                  <div class="pass-slip-row">
                    <span class="pass-slip-k">{{ slip.reference_no || ('#' + slip.id) }}</span>
                    <span class="pass-slip-v">{{ formatDateShort(slip.date_from) }}{{ slip.date_to && slip.date_to !== slip.date_from ? ' – ' + formatDateShort(slip.date_to) : '' }}</span>
                  </div>
                  <div class="pass-slip-row">
                    <span class="pass-slip-k">Out / In</span>
                    <span class="pass-slip-v">
                      {{ formatTime(slip.time_out) || '—' }}
                      →
                      {{ slip.do_not_return ? 'Do not return' : (formatTime(slip.time_in) || '—') }}
                    </span>
                  </div>
                  <div v-if="slip.reason" class="pass-slip-reason">{{ slip.reason }}</div>
                </div>
              </div>
            </div>

            <!-- Punches that don't fit the 4 canonical slots (e.g. a midday errand) -->
            <div v-if="detail.extra_punches && detail.extra_punches.length" class="extra-punches">
              <div class="extra-label">Additional punches</div>
              <div class="extra-list">
                <span v-for="(t, i) in detail.extra_punches" :key="i" class="extra-chip">
                  {{ formatTime(t) }}
                </span>
              </div>
            </div>

            <div v-if="detail.punch_logs && detail.punch_logs.length" class="punch-logs">
              <div class="extra-label">Today's punch log</div>
              <div class="punch-log-list">
                <div
                  v-for="(log, i) in detail.punch_logs"
                  :key="log.id || i"
                  class="punch-log-row"
                >
                  <div class="punch-log-main">
                    <span class="punch-log-badge" :class="log.inOutStatus === 'IN' ? 'log-in' : 'log-out'">
                      {{ log.inOutStatus }}
                    </span>
                    <span class="punch-log-time">{{ formatTime(log.time) }}</span>
                    <span class="punch-log-source">{{ log.source === 'web' ? 'System' : (log.source === 'pass_slip' ? 'Pass slip' : 'Biometric') }}</span>
                    <span v-if="log.has_photo" class="punch-log-photo-tag"><i class="bi bi-camera"></i></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Empty state -->
    <div v-if="!loading && !filtered.length" class="mon-empty">
      <i class="bi bi-inbox mon-empty-icon"></i>
      <div class="mon-empty-title">No employees found</div>
      <div class="mon-empty-text">{{ emptyMessage }}</div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import Chart from "chart.js/auto";
import { avatarUrlFor } from "@/utils/avatar";

// Attendance status palette — a reserved status set (each value always ships
// with a text label in the UI and chart legends). Desaturated ~12% from the
// old Bootstrap hues for a calmer read; contrast + CVD checked with the
// dataviz validator. "absent" is a deliberate neutral slate (means "no record").
const STATUS_COLORS = {
  in: "#1f9d57",
  out: "#2166d1",
  absent: "#767d8a",
  on_leave: "#c9770f",
  on_pass_slip: "#7a52c9",
  on_activity: "#0e8ba0",
};

const SKELETON_COUNT = 8;
const CHART_FONT = "Fira Sans, system-ui, sans-serif";

export default {
  name: "AttendanceMonitoring",
  data() {
    return {
      loading: false,
      hasLoadedOnce: false,
      skeletonCount: SKELETON_COUNT,
      employees: [],
      summary: { in: 0, out: 0, absent: 0, on_leave: 0, on_pass_slip: 0, on_activity: 0, total: 0 },
      date: "",
      activeTab: "all",
      search: "",
      officeFilter: "",
      refreshTimer: null,
      detail: null,
      avatarFailed: {},
      lastRefreshed: "",
      monthStart: "",
      monthPassSlips: [],
      passSlipsLoading: false,
      photoUrls: { out: null, in: null },
      photoLoading: false,
      photoSeq: 0,
      punchSlots: [
        { key: "morning_in", label: "Morning In", short: "AM In" },
        { key: "lunch_out", label: "Lunch Out", short: "Lunch" },
        { key: "lunch_in", label: "Lunch In", short: "PM In" },
        { key: "evening_out", label: "Evening Out", short: "Out" },
      ],
    };
  },
  computed: {
    formattedDate() {
      if (!this.date) return "";
      const d = new Date(this.date + "T00:00:00");
      return d.toLocaleDateString("en-PH", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    },
    offices() {
      const values = this.employees
        .map((e) => e.office)
        .filter(Boolean);
      return [...new Set(values)].sort();
    },
    emptyMessage() {
      if (this.search.trim() || this.officeFilter) {
        return "Try adjusting your search or office filter.";
      }
      if (this.activeTab !== "all") {
        return `No staff currently marked as "${this.statusLabel(this.activeTab)}".`;
      }
      return "No employee records are available for today.";
    },
    summaryCards() {
      const total = this.summary.total || 0;
      const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
      return [
        { key: "in", tab: "in", count: this.summary.in, pct: pct(this.summary.in), label: "In Office", icon: "bi-building-check" },
        { key: "out", tab: "out", count: this.summary.out, pct: pct(this.summary.out), label: "Left", icon: "bi-box-arrow-right" },
        { key: "absent", tab: "absent", count: this.summary.absent, pct: pct(this.summary.absent), label: "No Record", icon: "bi-person-dash" },
        { key: "on_leave", tab: "on_leave", count: this.summary.on_leave, pct: pct(this.summary.on_leave), label: "On Leave", icon: "bi-calendar2-x" },
        { key: "on_pass_slip", tab: "on_pass_slip", count: this.summary.on_pass_slip, pct: pct(this.summary.on_pass_slip), label: "Pass Slip", icon: "bi-door-open" },
        { key: "on_activity", tab: "on_activity", count: this.summary.on_activity, pct: pct(this.summary.on_activity), label: "On Activity", icon: "bi-calendar-event" },
        { key: "total", tab: "all", count: total, pct: 100, label: "Total Staff", icon: "bi-people" },
      ];
    },
    analytics() {
      const total = this.summary.total || 0;
      const punched = this.summary.in + this.summary.out;
      const away = this.summary.on_leave + this.summary.on_pass_slip + this.summary.on_activity;
      const lateCount = this.employees.filter((e) => e.is_late).length;
      const monthLateStaff = this.employees.filter((e) => (e.late_days_month || 0) > 0).length;
      const monthPassSlips = this.employees.reduce((sum, e) => sum + (e.pass_slip_count_month || 0), 0);
      return {
        punched,
        punchRate: total ? Math.round((punched / total) * 100) : 0,
        presenceRate: total ? Math.round((this.summary.in / total) * 100) : 0,
        lateCount,
        awayCount: away,
        monthLateStaff,
        monthPassSlips,
      };
    },
    officeChartData() {
      const buckets = {};
      this.employees.forEach((emp) => {
        const office = emp.office || "Unassigned";
        if (!buckets[office]) {
          buckets[office] = { in: 0, out: 0, absent: 0, on_leave: 0, on_pass_slip: 0, on_activity: 0 };
        }
        buckets[office][emp.status] = (buckets[office][emp.status] || 0) + 1;
      });
      const labels = Object.keys(buckets).sort();
      return {
        labels,
        in: labels.map((l) => buckets[l].in),
        out: labels.map((l) => buckets[l].out),
        absent: labels.map((l) => buckets[l].absent),
        on_leave: labels.map((l) => buckets[l].on_leave),
        on_pass_slip: labels.map((l) => buckets[l].on_pass_slip),
        on_activity: labels.map((l) => buckets[l].on_activity),
      };
    },
    // Grow the "By office" chart with its rows so the y-axis labels always fit
    // (no tiny nested scrollbar) instead of a fixed height.
    officeChartHeight() {
      const rows = this.officeChartData.labels.length || 1;
      return Math.min(520, Math.max(180, rows * 30 + 76));
    },
    filtered() {
      let list = this.employees;
      if (this.activeTab !== "all") list = list.filter((e) => e.status === this.activeTab);
      if (this.officeFilter) {
        list = list.filter((e) => e.office === this.officeFilter);
      }
      if (this.search.trim()) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (e) =>
            e.first_name.toLowerCase().includes(q) ||
            e.last_name.toLowerCase().includes(q) ||
            (e.office || "").toLowerCase().includes(q)
        );
      }
      const order = { in: 0, out: 1, on_leave: 2, on_pass_slip: 3, absent: 4 };
      return [...list].sort((a, b) => {
        const byStatus = (order[a.status] ?? 5) - (order[b.status] ?? 5);
        if (byStatus !== 0) return byStatus;
        return `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
      });
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("api/attendance/monitoring");
        this.employees = data.employees;
        this.summary   = data.summary;
        this.date      = data.date;
        this.monthStart = data.month_start || "";
        this.lastRefreshed = new Date().toLocaleTimeString("en-PH", {
          hour: "numeric",
          minute: "2-digit",
        });
        if (this.detail && this.detail.personnel_id) {
          const refreshed = this.employees.find(
            (e) => e.personnel_id === this.detail.personnel_id
          );
          if (refreshed) {
            // The open detail is now a fresh object. Its lunch-photo metadata
            // may have moved (a lunch punch happened, or dedup/classification
            // shifted which log is the lunch punch) — re-fetch the photo blobs
            // so a punch made after the detail was opened doesn't get stuck on
            // "Photo unavailable".
            const photosChanged =
              this.lunchPhotoSignature(refreshed) !==
              this.lunchPhotoSignature(this.detail);
            this.detail = refreshed;
            if (photosChanged) this.refreshLunchPhotos(refreshed);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
        this.hasLoadedOnce = true;
        this.renderCharts();
      }
    },
    showAvatar(emp) {
      return Boolean(emp && emp.personnel_id) && !this.avatarFailed[emp.personnel_id];
    },
    onAvatarError(emp) {
      if (!emp || !emp.personnel_id) return;
      this.$set(this.avatarFailed, emp.personnel_id, true);
    },
    avatarStyle(emp) {
      const palette = ["#4d8dff", "#2bb6a3", "#7c5cff", "#e0901f", "#d45087", "#21a366"];
      const key = `${emp.last_name || ""}${emp.first_name || ""}`;
      let hash = 0;
      for (let i = 0; i < key.length; i += 1) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash);
      }
      return { backgroundColor: palette[Math.abs(hash) % palette.length] };
    },
    punchTime(emp, idx) {
      const punches = emp && emp.punches;
      if (!punches || !punches[idx]) return "—";
      return this.formatTime(punches[idx]);
    },
    punchCellClass(emp, idx) {
      const punches = emp && emp.punches;
      const punch = punches && punches[idx];
      const keys = ["morning_late", "lunch_out_early", "lunch_in_late", "evening_out_early"];
      if (!punch) return "punch-cell-empty";
      if (this.violation(emp, keys[idx])) return "punch-cell-violation";
      return idx % 2 === 0 ? "punch-cell-in" : "punch-cell-out";
    },
    avatarUrl(personnelId) {
      return avatarUrlFor(personnelId);
    },
    initials(emp) {
      return `${(emp.first_name || "")[0] || ""}${(emp.last_name || "")[0] || ""}`.toUpperCase();
    },
    formatTime(t) {
      if (!t) return "";
      const [h, m] = t.split(":");
      const hr = parseInt(h);
      const ampm = hr >= 12 ? "PM" : "AM";
      const h12 = hr % 12 || 12;
      return `${h12}:${m} ${ampm}`;
    },
    statusLabel(s) {
      return {
        in: "In Office",
        out: "Left Office",
        absent: "No Record Today",
        on_leave: "On Leave",
        on_pass_slip: "On Pass Slip",
        on_activity: "On Activity",
      }[s] || s;
    },
    violation(emp, key) {
      return !!(emp.violations && emp.violations[key]);
    },
    lunchInLateLabel(emp) {
      const deadline = emp && emp.lunch_in_deadline;
      if (deadline && deadline !== "13:00:00") {
        return `Late (after ${this.formatTime(deadline)})`;
      }
      return "Late (after 1:00 PM)";
    },
    openDetail(emp) {
      this.clearPhotoUrls();
      this.detail = emp;
      this.monthPassSlips = [];
      this.loadDetailExtras(emp);
    },
    closeDetail() {
      this.clearPhotoUrls();
      this.detail = null;
      this.monthPassSlips = [];
    },
    lunchPhotoEmptyLabel(emp, slot) {
      const photo = emp && emp.lunch_photos && emp.lunch_photos[slot];
      if (!photo || !photo.time) return "No lunch punch";
      if (!photo.has_photo) return "No photo (biometric / no capture)";
      return "Photo unavailable";
    },
    formatDateShort(d) {
      if (!d) return "";
      const dt = new Date(String(d).slice(0, 10) + "T00:00:00");
      return dt.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
    },
    clearPhotoUrls() {
      ["out", "in"].forEach((key) => {
        if (this.photoUrls[key]) {
          URL.revokeObjectURL(this.photoUrls[key]);
        }
      });
      this.photoUrls = { out: null, in: null };
      // Invalidate any in-flight loadLunchPhotos() so its result can't land
      // after the detail was closed or switched to another employee.
      this.photoSeq += 1;
    },
    // Identity of the two lunch punches whose photos we display, so an
    // auto-refresh can tell when they've changed and a re-fetch is needed.
    lunchPhotoSignature(emp) {
      const photos = (emp && emp.lunch_photos) || {};
      const tag = (slot) => {
        const meta = photos[slot];
        return meta && meta.has_photo && meta.id ? String(meta.id) : "";
      };
      return `${tag("out")}|${tag("in")}`;
    },
    async refreshLunchPhotos(emp) {
      this.photoLoading = true;
      try {
        await this.loadLunchPhotos(emp);
      } finally {
        this.photoLoading = false;
      }
    },
    async loadDetailExtras(emp) {
      this.photoLoading = true;
      this.passSlipsLoading = true;
      try {
        await Promise.all([
          this.loadLunchPhotos(emp),
          this.loadMonthPassSlips(emp),
        ]);
      } finally {
        this.photoLoading = false;
        this.passSlipsLoading = false;
      }
    },
    async loadLunchPhotos(emp) {
      const seq = (this.photoSeq += 1);
      const slots = ["out", "in"];
      const urls = { out: null, in: null };
      await Promise.all(
        slots.map(async (slot) => {
          const meta = emp.lunch_photos && emp.lunch_photos[slot];
          if (!meta || !meta.has_photo || !meta.id) return;
          try {
            const res = await axios.get(`api/attendance/punch-photo/${meta.id}`, {
              responseType: "blob",
            });
            urls[slot] = URL.createObjectURL(res.data);
          } catch (e) {
            urls[slot] = null;
          }
        })
      );
      if (seq !== this.photoSeq) {
        // A newer load (or a close) started while we were fetching — drop this.
        ["out", "in"].forEach((k) => urls[k] && URL.revokeObjectURL(urls[k]));
        return;
      }
      this.clearPhotoUrls();
      this.photoUrls = urls;
    },
    async loadMonthPassSlips(emp) {
      if (!emp || !emp.personnel_id) {
        this.monthPassSlips = [];
        return;
      }
      const from = this.monthStart || (this.date ? this.date.slice(0, 8) + "01" : "");
      const to = this.date;
      if (!from || !to) {
        this.monthPassSlips = [];
        return;
      }
      try {
        const { data } = await axios.get("api/leave-pass-slips/approved-in-range", {
          params: {
            from,
            to,
            personnel_id: emp.personnel_id,
          },
        });
        const rows = Array.isArray(data) ? data : [];
        this.monthPassSlips = rows.filter((r) => r.type === "pass_slip");
      } catch (e) {
        this.monthPassSlips = [];
      }
    },
    renderChart(key, el, config) {
      if (!el) return;
      if (this.chartRegistry[key]) {
        this.chartRegistry[key].destroy();
      }
      this.chartRegistry[key] = new Chart(el, config);
    },
    renderCharts() {
      this.$nextTick(() => {
        this.renderStatusChart();
        this.renderOfficeChart();
      });
    },
    renderStatusChart() {
      const el = this.$refs.statusChart;
      if (!el || !this.summary.total) return;

      this.renderChart("status", el, {
        type: "doughnut",
        data: {
          labels: ["In Office", "Left", "No Record", "On Leave", "Pass Slip", "On Activity"],
          datasets: [{
            data: [
              this.summary.in,
              this.summary.out,
              this.summary.absent,
              this.summary.on_leave,
              this.summary.on_pass_slip,
              this.summary.on_activity,
            ],
            backgroundColor: [
              STATUS_COLORS.in,
              STATUS_COLORS.out,
              STATUS_COLORS.absent,
              STATUS_COLORS.on_leave,
              STATUS_COLORS.on_pass_slip,
              STATUS_COLORS.on_activity,
            ],
            // 2px surface gap between arcs instead of a stroke.
            borderColor: "#ffffff",
            borderWidth: 2,
            hoverOffset: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "68%",
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                boxWidth: 8,
                boxHeight: 8,
                usePointStyle: true,
                pointStyle: "circle",
                padding: 14,
                color: "#5a6474",
                font: { size: 11, family: CHART_FONT },
              },
            },
          },
        },
      });
    },
    renderOfficeChart() {
      const el = this.$refs.officeChart;
      const data = this.officeChartData;
      if (!el || !data.labels.length) return;

      const bar = (label, values, color) => ({
        label,
        data: values,
        backgroundColor: color,
        borderColor: "#ffffff",
        borderWidth: 1,
        borderRadius: 3,
        borderSkipped: false,
        maxBarThickness: 20,
      });

      this.renderChart("office", el, {
        type: "bar",
        data: {
          labels: data.labels,
          datasets: [
            bar("In Office", data.in, STATUS_COLORS.in),
            bar("Left", data.out, STATUS_COLORS.out),
            bar("No Record", data.absent, STATUS_COLORS.absent),
            bar("On Leave", data.on_leave, STATUS_COLORS.on_leave),
            bar("Pass Slip", data.on_pass_slip, STATUS_COLORS.on_pass_slip),
            bar("On Activity", data.on_activity, STATUS_COLORS.on_activity),
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: "y",
          scales: {
            x: {
              stacked: true,
              border: { display: false },
              grid: { color: "rgba(20,28,45,0.06)", drawBorder: false },
              ticks: { precision: 0, color: "#8b94a3", font: { size: 10, family: CHART_FONT } },
            },
            y: {
              stacked: true,
              border: { display: false },
              grid: { display: false, drawBorder: false },
              ticks: { color: "#5a6474", font: { size: 11, family: CHART_FONT } },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                boxWidth: 8,
                boxHeight: 8,
                usePointStyle: true,
                pointStyle: "circle",
                padding: 12,
                color: "#5a6474",
                font: { size: 11, family: CHART_FONT },
              },
            },
          },
        },
      });
    },
    destroyCharts() {
      Object.keys(this.chartRegistry).forEach((key) => {
        if (this.chartRegistry[key]) {
          this.chartRegistry[key].destroy();
        }
      });
      this.chartRegistry = {};
    },
  },
  created() {
    this.chartRegistry = {};
  },
  mounted() {
    this.load();
    this.refreshTimer = setInterval(this.load, 60000);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    this.destroyCharts();
    this.clearPhotoUrls();
  },
};
</script>

<style scoped>
/* ================================================================
   Attendance Monitoring — refined & calm pass
   Tokens live on the root; components read them so one accent and
   one neutral scale carry the whole page. Fira Sans loads 200–500,
   so 500 is the heaviest weight — hierarchy is size + colour + space.
   ================================================================ */
.attendance-monitoring {
  /* surfaces & lines */
  --am-page: #ebedef;
  --am-surface: #ffffff;
  --am-sunken: #f4f5f7;
  --am-border: #e7e9ee;
  --am-border-strong: #d8dbe1;

  /* ink */
  --am-text: #1b2233;
  --am-text-2: #5a6474;
  --am-text-3: #8b94a3;

  /* accent — kept in the Attendance section's blue family */
  --am-accent: #2f6fe0;
  --am-accent-weak: #eef4fd;
  --am-ring: rgba(47, 111, 224, 0.3);

  /* status palette — mirrors STATUS_COLORS in <script> */
  --am-in: #1f9d57;        --am-in-weak: #e9f6ef;
  --am-out: #2166d1;       --am-out-weak: #e9f0fc;
  --am-absent: #767d8a;    --am-absent-weak: #eef0f2;
  --am-leave: #c9770f;     --am-leave-weak: #fbf1e3;
  --am-pass: #7a52c9;      --am-pass-weak: #f1ecfa;
  --am-activity: #0e8ba0;  --am-activity-weak: #e4f4f7;
  --am-danger: #c0392b;    --am-danger-weak: #fbecea;

  /* radii */
  --am-r-xs: 6px;
  --am-r-sm: 8px;
  --am-r: 12px;
  --am-r-lg: 16px;

  /* elevation */
  --am-shadow-sm: 0 1px 2px rgba(20, 28, 45, 0.04), 0 1px 3px rgba(20, 28, 45, 0.05);
  --am-shadow-md: 0 6px 20px rgba(20, 28, 45, 0.08), 0 2px 6px rgba(20, 28, 45, 0.04);

  width: 100%;
  color: var(--am-text);
}
.attendance-monitoring * { box-sizing: border-box; }

/* ── Header ─────────────────────────────────────────────── */
.mon-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}
.mon-header-left { display: flex; align-items: center; gap: 12px; }
.mon-header-icon {
  width: 40px; height: 40px;
  border-radius: var(--am-r);
  background: var(--am-sunken);
  color: var(--am-accent);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
}
.mon-title { font-size: 1.0625rem; font-weight: 500; color: var(--am-text); letter-spacing: -0.01em; }
.mon-date  { font-size: 0.75rem; color: var(--am-text-3); }

.btn-refresh {
  border: 1px solid var(--am-border);
  background: var(--am-surface);
  border-radius: var(--am-r-sm);
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  color: var(--am-text-2);
  font-size: 0.95rem;
  box-shadow: var(--am-shadow-sm);
  transition: color 140ms ease, border-color 140ms ease;
}
.btn-refresh:hover { border-color: var(--am-accent); color: var(--am-accent); }
.btn-refresh.spinning i { animation: am-spin 0.7s linear infinite; }
@keyframes am-spin { to { transform: rotate(360deg); } }

/* ── KPI strip ──────────────────────────────────────────── */
.summary-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.summary-card {
  background: var(--am-surface);
  border: 1px solid var(--am-border);
  border-radius: var(--am-r);
  padding: 12px 14px 11px;
  cursor: pointer;
  box-shadow: var(--am-shadow-sm);
  transition: box-shadow 140ms ease, transform 200ms ease, background 140ms ease;
  outline: none;
}
.summary-card:hover { transform: translateY(-1px); box-shadow: var(--am-shadow-md); }
.summary-card:focus-visible { box-shadow: 0 0 0 3px var(--am-ring); }
.summary-card.active {
  background: var(--am-accent-weak);
  box-shadow: inset 0 0 0 1.5px var(--am-accent), var(--am-shadow-sm);
}
.summary-card.active:hover {
  transform: translateY(-1px);
  box-shadow: inset 0 0 0 1.5px var(--am-accent), var(--am-shadow-md);
}
.summary-top {
  display: flex; align-items: baseline; justify-content: space-between; gap: 6px;
  margin-bottom: 5px;
}
.summary-count {
  font-size: 1.5rem; font-weight: 500; line-height: 1;
  color: var(--am-text); letter-spacing: -0.02em;
}
.summary-pct { font-size: 0.75rem; font-weight: 500; color: var(--am-text-3); }
.summary-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.75rem; color: var(--am-text-2);
}
.summary-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.summary-meter {
  height: 3px; border-radius: 99px; background: var(--am-sunken);
  margin-top: 9px; overflow: hidden;
}
.summary-meter span { display: block; height: 100%; border-radius: 99px; transition: width 0.4s ease; }

/* status → dot + meter fill (shared name space with .emp-status-dot) */
.dot-in, .meter-in span                    { background: var(--am-in); }
.dot-out, .meter-out span                  { background: var(--am-out); }
.dot-absent, .meter-absent span            { background: var(--am-absent); }
.dot-on_leave, .meter-on_leave span        { background: var(--am-leave); }
.dot-on_pass_slip, .meter-on_pass_slip span { background: var(--am-pass); }
.dot-on_activity, .meter-on_activity span   { background: var(--am-activity); }
.dot-total, .meter-total span              { background: var(--am-accent); }

/* ── Analytics ──────────────────────────────────────────── */
.analytics-panel {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 1.4fr;
  gap: 10px;
  margin-bottom: 18px;
}
.analytics-card {
  background: var(--am-surface);
  border: 1px solid var(--am-border);
  border-radius: var(--am-r);
  padding: 15px 16px;
  box-shadow: var(--am-shadow-sm);
}
.analytics-head { margin-bottom: 12px; }
.analytics-head h6 { margin: 0; font-size: 0.8125rem; font-weight: 500; color: var(--am-text); }
.analytics-sub { font-size: 0.6875rem; color: var(--am-text-3); }
.insight-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.insight-item {
  background: var(--am-sunken);
  border-radius: var(--am-r-sm);
  padding: 9px 11px;
}
.insight-value {
  display: block; font-size: 1.25rem; font-weight: 500; line-height: 1.1;
  color: var(--am-text); letter-spacing: -0.01em;
}
.insight-value.insight-in    { color: var(--am-in); }
.insight-value.insight-warn  { color: var(--am-danger); }
.insight-value.insight-leave { color: var(--am-leave); }
.insight-label { display: block; margin-top: 3px; font-size: 0.6875rem; font-weight: 500; color: var(--am-text-2); }
.insight-note { display: block; margin-top: 1px; font-size: 0.6875rem; color: var(--am-text-3); }
.chart-wrap { position: relative; width: 100%; }
.chart-donut { height: 224px; }
.chart-bar { min-height: 180px; }
.chart-wide { min-width: 0; }

/* ── Controls (sticky) ──────────────────────────────────── */
.controls-sticky {
  position: sticky;
  top: 0;
  z-index: 20;
  background: var(--am-page);
  margin: 0 -1rem;
  padding: 8px 1rem 6px;
  box-shadow: 0 10px 12px -12px rgba(20, 28, 45, 0.16);
}
.mon-controls {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.mon-controls-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.mon-tabs { display: flex; gap: 4px; flex-wrap: wrap; }
.mon-tab {
  padding: 6px 12px; border-radius: var(--am-r-sm);
  border: 1px solid var(--am-border); background: var(--am-surface);
  font-size: 0.8125rem; font-weight: 400; color: var(--am-text-2);
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  transition: color 140ms ease, border-color 140ms ease, background 140ms ease;
}
.mon-tab:hover { border-color: var(--am-border-strong); color: var(--am-text); }
.mon-tab.active {
  background: var(--am-accent); border-color: var(--am-accent); color: #fff; font-weight: 500;
}
.mon-tab-badge {
  font-size: 0.6875rem; font-weight: 500; padding: 1px 6px; border-radius: 99px;
  background: var(--am-sunken); color: var(--am-text-2);
}
.badge-in           { background: var(--am-in-weak); color: var(--am-in); }
.badge-out          { background: var(--am-out-weak); color: var(--am-out); }
.badge-absent       { background: var(--am-absent-weak); color: var(--am-absent); }
.badge-on_leave     { background: var(--am-leave-weak); color: var(--am-leave); }
.badge-on_pass_slip { background: var(--am-pass-weak); color: var(--am-pass); }
.badge-on_activity  { background: var(--am-activity-weak); color: var(--am-activity); }
.mon-tab.active .mon-tab-badge { background: rgba(255, 255, 255, 0.22); color: #fff; }

.mon-office-filter, .mon-search {
  border: 1px solid var(--am-border);
  border-radius: var(--am-r-sm);
  font-size: 0.8125rem;
  color: var(--am-text);
  background: var(--am-surface);
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease;
}
.mon-office-filter { padding: 6px 10px; min-width: 150px; }
.mon-office-filter:focus, .mon-search:focus {
  border-color: var(--am-accent);
  box-shadow: 0 0 0 3px var(--am-ring);
}
.mon-search-wrap { position: relative; }
.mon-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--am-text-3); font-size: 0.8rem; pointer-events: none;
}
.mon-search { padding: 6px 12px 6px 30px; width: 200px; }

.mon-meta {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 8px;
  font-size: 0.6875rem; color: var(--am-text-3);
}

/* ── Employee grid ──────────────────────────────────────── */
.emp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  margin-top: 14px;
}
.emp-grid-refreshing { opacity: 0.55; pointer-events: none; transition: opacity 140ms ease; }

.emp-card {
  position: relative;
  background: var(--am-surface);
  border: 1px solid var(--am-border);
  border-radius: var(--am-r-lg);
  padding: 15px 15px 13px;
  cursor: pointer;
  box-shadow: var(--am-shadow-sm);
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 140ms ease;
}
.emp-card:hover {
  transform: translateY(-2px);
  border-color: var(--am-border-strong);
  box-shadow: var(--am-shadow-md);
}

.emp-card-top { display: flex; align-items: flex-start; gap: 11px; margin-bottom: 13px; }
.emp-avatar-wrap { flex-shrink: 0; }
.emp-avatar {
  width: 44px; height: 44px; border-radius: var(--am-r);
  overflow: hidden; box-shadow: var(--am-shadow-sm);
  background: var(--am-sunken);
}
.emp-avatar-img, .emp-avatar-fallback {
  width: 100%; height: 100%; object-fit: cover; display: block;
}
.emp-avatar-fallback {
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem; font-weight: 500; color: #fff;
}

.emp-identity { flex: 1; min-width: 0; padding-top: 1px; }
.emp-name-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.emp-name { margin: 0; font-size: 0.875rem; font-weight: 500; color: var(--am-text); line-height: 1.3; }
.emp-office {
  margin: 3px 0 0; font-size: 0.75rem; color: var(--am-text-2);
  display: flex; align-items: center; gap: 5px; line-height: 1.35;
}
.emp-office i { color: var(--am-text-3); font-size: 0.7rem; flex-shrink: 0; }

.emp-late-badge, .emp-web-badge {
  font-size: 0.625rem; font-weight: 500; padding: 1px 6px; border-radius: 99px;
  display: inline-flex; align-items: center; gap: 3px; letter-spacing: 0.02em;
  white-space: nowrap;
}
.emp-late-badge { background: var(--am-danger-weak); color: var(--am-danger); }
.emp-web-badge  { background: var(--am-accent-weak); color: var(--am-accent); }

.emp-status-pill {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.6875rem; font-weight: 500; padding: 4px 9px; border-radius: 99px;
  white-space: nowrap; flex-shrink: 0;
}
.emp-status-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

/* status pill colours — also used by .det-status-badge */
.status-in           { background: var(--am-in-weak); color: var(--am-in); }
.status-out          { background: var(--am-out-weak); color: var(--am-out); }
.status-absent       { background: var(--am-absent-weak); color: var(--am-absent); }
.status-on_leave     { background: var(--am-leave-weak); color: var(--am-leave); }
.status-on_pass_slip { background: var(--am-pass-weak); color: var(--am-pass); }
.status-on_activity  { background: var(--am-activity-weak); color: var(--am-activity); }

/* ── Punch board ────────────────────────────────────────── */
.emp-punch-board { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 11px; }
.punch-cell {
  border-radius: var(--am-r-sm); padding: 7px 5px; text-align: center;
  border: 1px solid var(--am-border); background: var(--am-surface);
}
.punch-cell-label {
  display: block; font-size: 0.5625rem; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--am-text-3); margin-bottom: 3px;
}
.punch-cell-time { display: block; font-size: 0.75rem; font-weight: 500; color: var(--am-text); line-height: 1.2; }
.punch-cell-in, .punch-cell-out { background: var(--am-sunken); }
.punch-cell-in .punch-cell-time  { color: var(--am-in); }
.punch-cell-out .punch-cell-time { color: var(--am-out); }
.punch-cell-violation { background: var(--am-danger-weak); border-color: #f0c9c4; }
.punch-cell-violation .punch-cell-time { color: var(--am-danger); }
.punch-cell-empty .punch-cell-time { color: var(--am-text-3); }

/* ── Card footer ────────────────────────────────────────── */
.emp-card-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding-top: 11px; border-top: 1px solid var(--am-border);
}
.emp-foot-times { display: flex; flex-wrap: wrap; gap: 5px; min-width: 0; }
.emp-time {
  font-size: 0.6875rem; font-weight: 500; padding: 3px 8px; border-radius: 99px;
  display: inline-flex; align-items: center; gap: 4px;
}
.emp-time-in       { background: var(--am-in-weak); color: var(--am-in); }
.emp-time-out      { background: var(--am-out-weak); color: var(--am-out); }
.emp-time-muted    { background: var(--am-sunken); color: var(--am-text-3); }
.emp-time-activity { background: var(--am-activity-weak); color: var(--am-activity); }

.emp-view-link {
  font-size: 0.6875rem; font-weight: 500; color: var(--am-accent);
  white-space: nowrap; display: inline-flex; align-items: center; gap: 2px;
  opacity: 0.8; transition: opacity 140ms ease, gap 140ms ease;
}
.emp-card:hover .emp-view-link { opacity: 1; gap: 4px; }

.emp-month-stats {
  display: flex; flex-wrap: wrap; gap: 5px;
  margin-top: 9px; padding-top: 9px; border-top: 1px solid var(--am-border);
}
.emp-stat-chip {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.6875rem; font-weight: 500; color: var(--am-text-2);
  background: var(--am-sunken); border-radius: 99px; padding: 3px 8px;
}
.emp-stat-chip.emp-stat-active { color: var(--am-pass); background: var(--am-pass-weak); }
.emp-stat-chip.emp-stat-warn   { color: var(--am-danger); background: var(--am-danger-weak); }

/* ── Skeleton (first load only) ─────────────────────────── */
.emp-skeleton {
  background: var(--am-surface);
  border: 1px solid var(--am-border);
  border-radius: var(--am-r-lg);
  padding: 15px 15px 13px;
  box-shadow: var(--am-shadow-sm);
}
.sk-block {
  display: block;
  background: linear-gradient(90deg, var(--am-sunken) 0%, #eceef1 50%, var(--am-sunken) 100%);
  background-size: 200% 100%;
  animation: am-shimmer 1.3s ease-in-out infinite;
  border-radius: var(--am-r-xs);
}
.sk-top { display: flex; gap: 11px; margin-bottom: 13px; }
.sk-avatar { width: 44px; height: 44px; border-radius: var(--am-r); flex-shrink: 0; }
.sk-lines { flex: 1; display: flex; flex-direction: column; gap: 7px; padding-top: 3px; }
.sk-line { height: 10px; width: 75%; }
.sk-line-sm { height: 8px; width: 45%; }
.sk-board { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 11px; }
.sk-board .sk-block { height: 40px; }
.sk-foot { height: 26px; width: 60%; }
@keyframes am-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Empty state ────────────────────────────────────────── */
.mon-empty {
  text-align: center; padding: 44px 20px; margin-top: 14px;
  color: var(--am-text-3);
  background: var(--am-surface);
  border: 1px dashed var(--am-border-strong);
  border-radius: var(--am-r-lg);
}
.mon-empty-icon { font-size: 1.9rem; color: var(--am-text-3); display: block; margin-bottom: 8px; opacity: 0.7; }
.mon-empty-title { font-size: 0.9375rem; font-weight: 500; color: var(--am-text-2); margin-bottom: 3px; }
.mon-empty-text { font-size: 0.8125rem; color: var(--am-text-3); }

/* ── Detail modal ───────────────────────────────────────── */
.det-backdrop {
  position: fixed; inset: 0; background: rgba(15, 20, 30, 0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050; padding: 16px;
}
.det-modal {
  background: var(--am-surface); border-radius: var(--am-r-lg);
  width: 100%; max-width: 560px;
  max-height: calc(100vh - 48px); overflow: auto;
  box-shadow: 0 24px 64px rgba(15, 20, 30, 0.24);
}
.det-header {
  display: flex; align-items: center; gap: 13px;
  padding: 18px 20px 15px; border-bottom: 1px solid var(--am-border);
}
.det-avatar {
  width: 50px; height: 50px; border-radius: var(--am-r); flex-shrink: 0;
  overflow: hidden; background: var(--am-sunken); box-shadow: var(--am-shadow-sm);
}
.det-avatar .emp-avatar-img, .det-avatar .emp-avatar-fallback { width: 100%; height: 100%; }
.det-avatar .emp-avatar-fallback {
  display: flex; align-items: center; justify-content: center;
  font-size: 1.05rem; font-weight: 500; color: #fff;
}
.det-identity { flex: 1; min-width: 0; }
.det-name { font-size: 0.9375rem; font-weight: 500; color: var(--am-text); letter-spacing: -0.01em; }
.det-meta { display: flex; align-items: center; gap: 7px; margin-top: 4px; flex-wrap: wrap; }
.det-div { font-size: 0.75rem; color: var(--am-text-3); }
.det-status-badge { font-size: 0.6875rem; font-weight: 500; padding: 2px 8px; border-radius: 99px; }
.det-activity-title {
  font-size: 0.6875rem; font-weight: 500; color: var(--am-activity);
  background: var(--am-activity-weak); border-radius: var(--am-r-xs);
  padding: 2px 8px; display: inline-flex; align-items: center;
  max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.det-close {
  width: 30px; height: 30px; border-radius: var(--am-r-sm); border: 1px solid var(--am-border);
  background: var(--am-surface); cursor: pointer; color: var(--am-text-2); font-size: 0.78rem;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: color 140ms ease, border-color 140ms ease;
}
.det-close:hover { border-color: var(--am-border-strong); color: var(--am-text); }
.det-body { padding: 18px 20px; }

.det-month-bar { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 14px; }
.det-month-stat {
  background: var(--am-sunken); border-radius: var(--am-r-sm);
  padding: 10px 12px; text-align: center;
}
.det-month-value {
  display: block; font-size: 1.25rem; font-weight: 500; color: var(--am-text);
  line-height: 1.1; letter-spacing: -0.01em;
}
.det-month-value.text-danger { color: var(--am-danger) !important; }
.det-month-label { display: block; font-size: 0.625rem; font-weight: 500; color: var(--am-text-3); margin-top: 3px; }
.det-section { margin-top: 15px; }
.extra-label {
  font-size: 0.6875rem; font-weight: 500; color: var(--am-text-3); margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.04em;
}

.punch-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.punch-slot { border-radius: var(--am-r-sm); padding: 13px; border: 1px solid var(--am-border); background: var(--am-surface); }
.slot-filled, .slot-filled.slot-out { background: var(--am-sunken); }
.slot-empty { background: var(--am-surface); }
.slot-violation { background: var(--am-danger-weak) !important; border-color: #f0c9c4 !important; }
.punch-violation { font-size: 0.625rem; font-weight: 500; color: var(--am-danger); margin-top: 4px; }
.punch-label {
  font-size: 0.625rem; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--am-text-3); margin-bottom: 6px;
}
.punch-label i { color: var(--am-text-3); }
.punch-time { font-size: 1.125rem; font-weight: 500; color: var(--am-text); line-height: 1; letter-spacing: -0.01em; }
.slot-empty .punch-time { color: var(--am-text-3); }

.lunch-photo-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.lunch-photo-card { border: 1px solid var(--am-border); border-radius: var(--am-r-sm); overflow: hidden; background: var(--am-surface); }
.lunch-photo-head {
  display: flex; justify-content: space-between; align-items: center; gap: 6px;
  padding: 7px 10px; background: var(--am-sunken); border-bottom: 1px solid var(--am-border);
  font-size: 0.6875rem; font-weight: 500; color: var(--am-text-2);
}
.lunch-photo-head small { color: var(--am-text-3); font-weight: 400; }
.lunch-photo-body { min-height: 140px; display: flex; align-items: center; justify-content: center; background: var(--am-sunken); }
.lunch-photo-img { width: 100%; height: 160px; object-fit: cover; display: block; }
.lunch-photo-empty { font-size: 0.6875rem; color: var(--am-text-3); text-align: center; padding: 16px 10px; }

.pass-slip-list { display: grid; gap: 7px; }
.pass-slip-card { border: 1px solid var(--am-border); border-radius: var(--am-r-sm); padding: 10px 12px; background: var(--am-sunken); }
.pass-slip-row { display: flex; justify-content: space-between; gap: 10px; font-size: 0.75rem; margin-bottom: 3px; }
.pass-slip-row:last-child { margin-bottom: 0; }
.pass-slip-k { color: var(--am-text-3); font-weight: 400; }
.pass-slip-v { color: var(--am-text); font-weight: 500; text-align: right; }
.pass-slip-reason { font-size: 0.72rem; color: var(--am-text-2); margin-top: 4px; line-height: 1.35; }

.extra-punches { margin-top: 14px; }
.extra-list { display: flex; flex-wrap: wrap; gap: 5px; }
.extra-chip { background: var(--am-sunken); border-radius: 99px; padding: 3px 10px; font-size: 0.75rem; font-weight: 500; color: var(--am-text-2); }

.punch-logs { margin-top: 15px; }
.punch-log-list { display: grid; gap: 6px; }
.punch-log-row {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 9px 11px; border: 1px solid var(--am-border); border-radius: var(--am-r-sm); background: var(--am-surface);
}
.punch-log-main { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-width: 0; }
.punch-log-badge { font-size: 0.625rem; font-weight: 500; padding: 2px 7px; border-radius: 99px; }
.punch-log-badge.log-in  { background: var(--am-in-weak); color: var(--am-in); }
.punch-log-badge.log-out { background: var(--am-out-weak); color: var(--am-out); }
.punch-log-time { font-size: 0.8rem; font-weight: 500; color: var(--am-text); }
.punch-log-source { font-size: 0.6875rem; color: var(--am-text-3); }
.punch-log-photo-tag { color: var(--am-accent); font-size: 0.78rem; }

.text-muted.small { font-size: 0.75rem; color: var(--am-text-3); }

/* Modal transition */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.16s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 992px) {
  .analytics-panel { grid-template-columns: 1fr 1fr; }
  .chart-wide { grid-column: 1 / -1; }
  .emp-grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
}
@media (max-width: 768px) {
  .analytics-panel { grid-template-columns: 1fr; }
  .emp-card-top { flex-wrap: wrap; }
  .emp-status-pill { width: 100%; justify-content: center; margin-top: 2px; }
}
@media (max-width: 576px) {
  .mon-controls { flex-direction: column; align-items: stretch; }
  .mon-controls-right { flex-direction: column; align-items: stretch; }
  .mon-search, .mon-search-wrap, .mon-office-filter { width: 100%; }
  .emp-grid { grid-template-columns: 1fr; }
  .emp-view-link { display: none; }
  .punch-grid, .det-month-bar, .lunch-photo-grid { grid-template-columns: 1fr; }
}

/* ── Reduced motion ─────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .summary-card, .emp-card, .emp-view-link, .summary-meter span { transition: none; }
  .summary-card:hover, .emp-card:hover, .summary-card.active:hover { transform: none; }
  .btn-refresh.spinning i, .sk-block { animation: none; }
  .sk-block { background: var(--am-sunken); }
}
</style>
